/**
 * HR — ติดตามการประเมิน (/hr/evaluations).
 * เห็นรอบประเมินทั้งหมด + ความคืบหน้า (กรอกแล้ว/ยัง) + ส่งให้ผู้บริหารตัดสินเมื่อครบ
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useHrEvaluations, useSendEvalToExec, useRemindEval } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { ChevronRight, Send, BellRing } from 'lucide-react';

interface Cycle {
  id: string; subject_name: string; subject_dept: string; title: string;
  due_date: string; status: string; decision: string | null; done: number; total: number; built: boolean;
}

const STATUS_TH: Record<string, { label: string; cls: string }> = {
  collecting: { label: 'กำลังเก็บแบบประเมิน', cls: 'bg-amber-50 text-amber-700' },
  pending_decision: { label: 'รอผู้บริหารตัดสิน', cls: 'bg-blue-50 text-blue-700' },
  completed: { label: 'เสร็จสิ้น', cls: 'bg-green-50 text-green-700' },
};
const DECISION_TH: Record<string, string> = {
  pass: 'ผ่าน', pass_conditional: 'ผ่านแบบมีเงื่อนไข', needs_improve: 'ต้องปรับปรุง', fail: 'ไม่ผ่าน',
};

export function HREvaluations() {
  const { data, isLoading } = useHrEvaluations();
  const send = useSendEvalToExec();
  const remind = useRemindEval();
  const toast = useToast();
  const nav = useNavigate();
  const cycles = (data?.cycles ?? []) as unknown as Cycle[];
  const [remindingId, setRemindingId] = useState<string | null>(null);

  async function onSend(c: Cycle) {
    try {
      await send.mutateAsync(c.id);
      toast.success('ส่งให้ผู้บริหารตัดสินแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งไม่สำเร็จ');
    }
  }

  async function onRemind(c: Cycle) {
    setRemindingId(c.id);
    try {
      const r = await remind.mutateAsync(c.id);
      toast.success(r.sent > 0 ? `ส่งเตือนแล้ว ${r.sent} คน` : 'ไม่มีคนที่ต้องเตือน (หรือยังไม่ผูก LINE)');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งเตือนไม่สำเร็จ');
    } finally {
      setRemindingId(null);
    }
  }

  return (
    <AdminLayout title="ติดตามการประเมิน">
      {isLoading ? (
        <div className="py-10 text-center text-slate-400 text-sm">กำลังโหลด…</div>
      ) : cycles.length === 0 ? (
        <div className="py-10 text-center text-slate-400 text-sm">ยังไม่มีรอบประเมินที่เปิด</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3 font-medium">พนักงาน</th>
                <th className="p-3 font-medium">รอบ</th>
                <th className="p-3 font-medium whitespace-nowrap">ครบกำหนด</th>
                <th className="p-3 font-medium">ความคืบหน้า</th>
                <th className="p-3 font-medium">สถานะ</th>
                <th className="p-3 font-medium text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((c) => {
                const complete = c.total > 0 && c.done >= c.total;
                // ยังไม่ถึงรอบ — รายชื่อผู้ประเมินจะถูกสร้างอัตโนมัติ ~10 วันก่อนครบกำหนด
                const notOpen = !c.built && c.status === 'collecting';
                const st = notOpen
                  ? { label: 'ยังไม่เปิดรอบ', cls: 'bg-slate-100 text-slate-500' }
                  : STATUS_TH[c.status] ?? { label: c.status, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="p-3">
                      <div className="font-medium text-slate-800">{c.subject_name}</div>
                      <div className="text-xs text-slate-400">{c.subject_dept || '—'}</div>
                    </td>
                    <td className="p-3 text-slate-700">{c.title}</td>
                    <td className="p-3 whitespace-nowrap text-slate-600">{c.due_date}</td>
                    <td className="p-3">
                      {notOpen ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <span className={complete ? 'text-green-600 font-medium' : 'text-slate-600'}>
                          {c.done} / {c.total}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      {c.decision && <div className="text-xs text-slate-500 mt-1">ผล: {DECISION_TH[c.decision] ?? c.decision}</div>}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {c.status === 'collecting' && c.built && !complete && (
                        <button
                          onClick={() => onRemind(c)}
                          disabled={remindingId === c.id}
                          title="ส่งแจ้งเตือน LINE ให้คนที่ยังไม่กรอก"
                          className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 px-2.5 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40 mr-2"
                        >
                          <BellRing className="w-3.5 h-3.5" /> {remindingId === c.id ? 'กำลังส่ง…' : 'ส่งเตือน'}
                        </button>
                      )}
                      {c.status === 'collecting' && c.built && (
                        <button
                          onClick={() => onSend(c)}
                          disabled={!complete || send.isPending}
                          title={complete ? '' : 'ยังกรอกไม่ครบ'}
                          className="inline-flex items-center gap-1 bg-slate-900 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
                        >
                          <Send className="w-3.5 h-3.5" /> ส่งผู้บริหาร
                        </button>
                      )}
                      <button
                        onClick={() => nav(`/hr/evaluations/${c.id}`)}
                        className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-xs ml-2"
                      >
                        ดูรายละเอียด <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
